import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { STUDY_MODE_TECHNIQUES } from "@/lib/chat/studyModeTechniques";

interface ChatInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (instruction: { id: string; name: string; content: string }) => void;
}

type SelectedOption = string | "custom";

export const ChatInstructionsDialog: React.FC<ChatInstructionsDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
}) => {
  const [selectedOption, setSelectedOption] = useState<SelectedOption>(
    STUDY_MODE_TECHNIQUES[0].id
  );
  const [promptName, setPromptName] = useState("");
  const [promptContent, setPromptContent] = useState("");

  const reset = () => {
    setSelectedOption(STUDY_MODE_TECHNIQUES[0].id);
    setPromptName("");
    setPromptContent("");
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOption === "custom") {
      if (!promptName.trim() || !promptContent.trim()) return;
      onSelect({
        id: `custom-${Date.now()}`,
        name: promptName.trim(),
        content: promptContent.trim(),
      });
    } else {
      const technique = STUDY_MODE_TECHNIQUES.find((t) => t.id === selectedOption);
      if (!technique) return;
      onSelect({
        id: `study-mode:${technique.id}`,
        name: technique.label,
        content: technique.systemPromptAddendum,
      });
    }
    reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  const isValid =
    selectedOption !== "custom" || (promptName.trim() && promptContent.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-xl p-0 gap-0">
        <DialogHeader className="py-4 px-6 pb-0 mb-4">
          <DialogTitle className="text-lg font-normal">Chat instructions</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose a learning technique or create your own prompt
          </p>
        </DialogHeader>

        <form onSubmit={handleCreate}>
          <div className="flex flex-1 flex-col gap-3 px-6 py-2 pt-0">
            <div className="space-y-6">
              <div className="space-y-3">
                <span className="text-sm font-medium">Techniques</span>

                <TooltipProvider delayDuration={300}>
                  <div className="overflow-hidden rounded-xl border divide-y">
                    {STUDY_MODE_TECHNIQUES.map((technique) => {
                      const isSelected = selectedOption === technique.id;
                      return (
                        <Tooltip key={technique.id}>
                          <TooltipTrigger asChild>
                            <div
                              onClick={() => setSelectedOption(technique.id)}
                              className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors ${
                                isSelected ? "bg-primary/5" : "hover:bg-accent/50"
                              }`}
                            >
                              <div
                                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                                  isSelected ? "border-primary" : "border-muted-foreground"
                                }`}
                              >
                                {isSelected && (
                                  <div className="h-2 w-2 rounded-full bg-primary" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{technique.label}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs text-xs">
                            {technique.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>

                {/* Create new prompt */}
                <div
                  onClick={() => setSelectedOption("custom")}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                    selectedOption === "custom" ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  <div
                    className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                      selectedOption === "custom" ? "border-primary" : "border-muted-foreground"
                    }`}
                  >
                    {selectedOption === "custom" && (
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium">Create new prompt</span>
                  </div>
                </div>
              </div>

              {/* Custom Prompt Fields */}
              {selectedOption === "custom" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="newPromptName">Prompt Name</Label>
                    <Input
                      id="newPromptName"
                      placeholder="Enter prompt name"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value)}
                      required
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPromptContent">Prompt Content</Label>
                    <Textarea
                      id="newPromptContent"
                      placeholder="Enter your custom chat prompt"
                      value={promptContent}
                      onChange={(e) => setPromptContent(e.target.value)}
                      rows={4}
                      required
                      className="mt-1 resize-none rounded-xl p-3 max-h-[80px] min-h-[45px]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <footer className="flex flex-row gap-2 px-6 py-4 justify-end">
            <Button type="button" variant="secondary" className="h-10 px-4 py-2 rounded-lg" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" className="h-10 px-4 py-2 rounded-lg" disabled={!isValid}>
              Create
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
};
